// frontend/src/pages/BlogPage.jsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, Clock, Eye, Search, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { PageSEO } from '../components/SEO/PageSEO';
import api from '../services/api';

export const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categories, setCategories] = useState([]);
  const postsPerPage = 6;

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await api.get('/cms/blog/', {
          params: {
            page: currentPage,
            page_size: postsPerPage,
            search: searchTerm || undefined,
            ordering: '-published_at'
          }
        });
        
        setPosts(response.data.results || []);
        setTotalPages(Math.ceil(response.data.count / postsPerPage));
        
        const uniqueCategories = [...new Set(response.data.results.map(post => post.page_type || 'blog'))];
        setCategories(uniqueCategories);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [currentPage, searchTerm]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <PageSEO
        title="وبلاگ | سولار پلتفرم"
        description="آخرین اخبار، مقالات و راهنماهای دنیای انرژی خورشیدی | آموزش نصب و راه‌اندازی نیروگاه خورشیدی"
        keywords="وبلاگ خورشیدی, مقاله انرژی خورشیدی, آموزش نصب پنل, اخبار انرژی خورشیدی"
      />

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">وبلاگ سولار پلتفرم</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              آخرین اخبار، مقالات و راهنماهای دنیای انرژی خورشیدی
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <main className="lg:w-3/4">
              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="جستجو در مقالات..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="w-full border border-gray-300 rounded-lg py-3 pr-12 pl-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {posts.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl">
                  <p className="text-gray-500">هیچ مقاله‌ای یافت نشد.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {posts.map((post) => (
                    <article key={post.id} className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition">
                      <Link to={`/blog/${post.slug}`}>
                        <img
                          src={post.featured_image || '/placeholder-image.jpg'}
                          alt={post.title}
                          className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                          onError={(e) => { e.target.src = '/placeholder-image.jpg'; }}
                        />
                      </Link>
                      <div className="p-5">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
                            {post.page_type === 'blog' ? 'مقاله' : (post.page_type || 'مطلب')}
                          </span>
                          <div className="flex items-center gap-1 text-gray-500 text-xs">
                            <Calendar className="w-3 h-3" />
                            {new Date(post.published_at).toLocaleDateString('fa-IR')}
                          </div>
                          <div className="flex items-center gap-1 text-gray-500 text-xs">
                            <Clock className="w-3 h-3" />
                            {post.read_time || Math.ceil((post.content?.length || 0) / 1000)} دقیقه
                          </div>
                        </div>
                        <Link to={`/blog/${post.slug}`}>
                          <h2 className="text-xl font-bold text-gray-900 mb-2 hover:text-blue-600 transition line-clamp-2">
                            {post.title}
                          </h2>
                        </Link>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                          {post.excerpt || 'برای مطالعه کامل مقاله کلیک کنید...'}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <User className="w-4 h-4" />
                            {post.author?.username || 'مدیر سایت'}
                          </div>
                          <div className="flex items-center gap-1 text-sm text-gray-400">
                            <Eye className="w-4 h-4" />
                            {post.view_count || 0}
                          </div>
                        </div>
                        <Link
                          to={`/blog/${post.slug}`}
                          className="mt-4 inline-block text-blue-600 hover:text-blue-700 font-medium"
                        >
                          ادامه مطلب →
                        </Link>
                      </div>
                    </article>
                  ))}
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`px-4 py-2 rounded-lg transition ${
                        currentPage === i + 1
                          ? 'bg-blue-600 text-white'
                          : 'border hover:bg-gray-50'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                </div>
              )}
            </main>

            {/* Sidebar */}
            <aside className="lg:w-1/4">
              <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">دسته‌بندی‌ها</h3>
                <div className="space-y-2">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSearchTerm(category)}
                      className="w-full text-right px-3 py-2 rounded-lg hover:bg-gray-50 flex justify-between items-center"
                    >
                      <span>{category === 'blog' ? 'مقالات' : category}</span>
                      <span className="text-gray-400 text-sm">
                        {posts.filter(p => p.page_type === category).length}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">محبوب‌ترین مقالات</h3>
                <div className="space-y-4">
                  {[...posts].sort((a, b) => (b.view_count || 0) - (a.view_count || 0)).slice(0, 4).map((post) => (
                    <Link key={post.id} to={`/blog/${post.slug}`} className="flex gap-3 group">
                      <img 
                        src={post.featured_image || '/placeholder-image.jpg'} 
                        alt="" 
                        className="w-16 h-16 rounded-lg object-cover" 
                        onError={(e) => { e.target.src = '/placeholder-image.jpg'; }}
                      />
                      <div>
                        <h4 className="font-medium text-gray-900 group-hover:text-blue-600 line-clamp-2">
                          {post.title}
                        </h4>
                        <p className="text-xs text-gray-400 mt-1">{post.view_count || 0} بازدید</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
};